import React, {FC, memo, useState} from 'react';
import {Flex} from '@priority-software/priority-style-react';
import {ToolbarPlugin} from '../ToolbarPlugin/ToolbarPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {useEditor} from '../hooks/useEditor';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {ExposeEditorInstancePlugin} from '../../../../../../components/LexicalEditor/plugins/ExposeEditorInstancePlugin';
import cx from 'classnames';
import {
    AutoLinkPlugin,
    DragDropPaste,
    DoubleClickLinkPlugin,
    HistoryPlugin,
    HorizontalRulePlugin,
    ImagesPlugin,
    LinkPlugin,
    ListPlugin
} from '../../../../../../components/LexicalEditor/plugins';
import {EmojisPlugin} from '../../../../../../components/LexicalEditor/plugins/EmojisPlugin';
import {CheckListPlugin} from '@lexical/react/LexicalCheckListPlugin';
import {InsertHtmlAndImagesPlugin} from './plugins/InsertHtmlAndImagesPlugin';
import {ThemeUpdater} from '../../../../../../components/LexicalEditor/plugins/ThemeUpdater';
import {DisableNestingPlugin} from './plugins/DisableNestingPlugin';
import {CustomPastePlugin} from './plugins/CustomPastePlugin';
import {ETargetAutoFill} from '../../types';
import {useHandlePressKey} from '../hooks/useHandleKeyPress';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {useBrowserContextMenu} from '../hooks/useBrowserContextMenu';
import ToastModal from '../../../../../../components/Toast/component/Toast';
import {isRTL} from '../../../../../../Localization';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {ParagraphReplacementPlugin} from '../../../../../../components/LexicalEditor/plugins/ParagraphReplacementPlugin';
import {TollbarTextsContext} from '../../../../../../components/LexicalEditor/components/Toolbar.parts/Toolbar/TollbarText.context';
import styles from './styles.module.scss';
import {texts} from '../../../../texts';
import {showFormClientSideMessage} from '../../../../../Form/actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';

interface IEditorProps {
    targetAutoFill: React.MutableRefObject<ETargetAutoFill>;
}

const emailEditorToastId = 'email-editor-toast-container';

export const Editor: FC<IEditorProps> = memo(({targetAutoFill}) => {
    const {
        editorDivRef,
        states,
        editorConfig,
        setEditorInstance,
        setFocusSubject,
        isFromTextEditor
    } = useEditor();
    const currentTemplate = states.templates[states.language];

    const toastRef = React.useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useHandlePressKey({target: editorDivRef.current, isDropdownOpen, setIsDropdownOpen});
    useBrowserContextMenu();

    const showSuccessToast = (message: string) => {
        if (toastRef.current) {
            toastRef.current.showToast(message, 'success', {
                containerId: emailEditorToastId,
                position: isRTL() ? 'bottom-left' : 'bottom-right',
                className: 'editor-toast-success'
            });
        }
    };

    const isValidImageType = (fileName: string) => {
        const isValid = /\.(jpe?g|png|gif)$/i.test(fileName);
        if (!isValid) {
            showFormClientSideMessage('', priority.netitems.shared.Message.Type.Error, {
                content: texts.only_image_files_allowed,
                confirmButtonText: texts.check_mail_weight_btn
            });
            return false;
        }
        return true;
    };

    return (
        <LexicalComposer key={states.language} initialConfig={editorConfig.current}>
            <TollbarTextsContext.Provider value={texts}>
                <ToolbarPlugin {...{setIsDropdownOpen, showSuccessToast}} />
            </TollbarTextsContext.Provider>
            <Flex
                {...createDataTestId(`email-editor-container`, true)}
                direction="column"
                componentClasses={cx(styles.flexContainer, styles.gap)}
                style={{overflow: 'hidden'}}
                alignItems="flex-start"
                justifyContent="flex-start"
            >
                <div className={styles.editorInputWrap}>
                    <RichTextPlugin
                        key={states.language}
                        contentEditable={
                            <ContentEditable
                                data-testid={'email-editor-content-editable'}
                                onFocus={() => {
                                    setFocusSubject(false);
                                    targetAutoFill.current = ETargetAutoFill.EDITOR;
                                }}
                                className={cx(styles.editorInput, styles.flexContainer)}
                                ref={editorDivRef}
                                contentEditable={true}
                            />
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <ToastModal
                        ref={toastRef}
                        containerId={emailEditorToastId}
                        componentClasses={styles.toastContainer}
                    />
                </div>

                <ExposeEditorInstancePlugin callback={setEditorInstance} />
                <ImagesPlugin />
                <EmojisPlugin />
                <LinkPlugin />
                <AutoLinkPlugin />
                <HistoryPlugin externalHistoryState={null} delay={500} />
                <ListPlugin />
                <CheckListPlugin />
                <HorizontalRulePlugin />
                <InsertHtmlAndImagesPlugin
                    htmlString={currentTemplate?.emailHtml}
                    isFromTextEditor={isFromTextEditor}
                />
                <ThemeUpdater isRtl={states.language === '1'} />
                <DisableNestingPlugin />
                <CustomPastePlugin isValidImageType={isValidImageType} />
                <DoubleClickLinkPlugin />
                <DragDropPaste
                    isDraggable={false}
                    shouldCheckSize={true}
                    isValidImageType={isValidImageType}
                />
                <ParagraphReplacementPlugin />
            </Flex>
        </LexicalComposer>
    );
});
